import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, setToken } from './api';

/**
 * Sey-X DICT Console is a single workspace (like StaffDesk, unlike GovFin's
 * ministry/finance/treasury split) — every persona lands in the same shell
 * and the UI gates by role, not by workspace. Two role shapes exist, read
 * straight out of packages/modules/c12-seyx/src/routes/*.ts's
 * requireRoles()/inline checks:
 *   - bare `SEYX_ADMIN` (DICT) — onboarding, activate/suspend, cert revoke,
 *     the member directory listing, access-right approve/reject/revoke, and
 *     the traffic dashboard are ALL gated to this role alone.
 *   - scoped `MEMBER_ADMIN:<mdaCode>` (a member MDA's own admin) — may
 *     register certs/services only for their own mdaCode, and request
 *     access rights only as their own mdaCode as consumer. They can also
 *     see the (unfiltered) access-rights list and derived topology.
 */
export interface Persona {
  id: string;
  name: string;
  title: string;
  org: string;
  /** The bearer token's own `sub` claim — lets the UI badge "requested by you". */
  sub: string;
  roles: string[];
  trustTier?: string;
}

/** mdaCodes this persona administers, derived from `MEMBER_ADMIN:<mda>` role claims. */
export function memberAdminCodes(roles: string[]): string[] {
  return roles.filter((r) => r.startsWith('MEMBER_ADMIN:')).map((r) => r.slice('MEMBER_ADMIN:'.length));
}

export interface AuthConfig {
  oneCitizen: boolean;
  devLogin: boolean;
}

interface AuthState {
  persona: Persona | null;
  personas: Persona[];
  config: AuthConfig;
  loading: boolean;
  login: (personaId: string) => Promise<void>;
  /** Store a session issued by the gateway (One Citizen sign-in). */
  completeLogin: (token: string, persona: Persona) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [persona, setPersona] = useState<Persona | null>(() => {
    const raw = localStorage.getItem('seyx.persona');
    return raw ? (JSON.parse(raw) as Persona) : null;
  });
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [config, setConfig] = useState<AuthConfig>({ oneCitizen: false, devLogin: true });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get<{ items: Persona[] }>('/auth/personas').catch(() => ({ items: [] as Persona[] })),
      api.get<AuthConfig>('/auth/config').catch(() => ({ oneCitizen: false, devLogin: true })),
    ])
      .then(([p, c]) => {
        setPersonas(p.items);
        setConfig(c);
      })
      .finally(() => setLoading(false));
  }, []);

  const completeLogin = useCallback((token: string, p: Persona) => {
    setToken(token);
    localStorage.setItem('seyx.persona', JSON.stringify(p));
    setPersona(p);
  }, []);

  const login = useCallback(
    async (personaId: string) => {
      const r = await api.post<{ token: string; persona: Persona }>('/auth/dev-login', { personaId });
      completeLogin(r.token, r.persona);
    },
    [completeLogin],
  );

  const logout = useCallback(() => {
    setToken(null);
    localStorage.removeItem('seyx.persona');
    setPersona(null);
  }, []);

  const value = useMemo(
    () => ({ persona, personas, config, loading, login, completeLogin, logout }),
    [persona, personas, config, loading, login, completeLogin, logout],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}
